import {
    BadRequestError,
    ForbiddenError,
    HttpError,
    ValidationFailuresResponse,
} from "components/ServerValidationErrors";

import { PartitionsApiUrl } from "env-config";
import { acquireAccessToken } from "./access-token-service";

const baseApiUrl = (): string => PartitionsApiUrl().replace(/[/]$/, "");

//#region Type definitions

export type guid = string;

export type CreatePartitionRequest = {
    fieldCompanyId: number;
    parentHierarchyNodeId?: number | null;
    name: string;
    reference: string | null;
    ownerEmail: string | null;
    notes: string;
};

export type CreatePartitionResponse = {
    id: guid;
    callOffCode: string;
};

export type UpdatePartitionRequest = {
    name: string;
    reference: string | null;
    ownerEmail: string | null;
    notes: string;
};

export type UpdatePartitionCallOffCodeRequest = {
    callOffCodes: string[];
};

export type GetPartitionByIdResponse = {
    id: guid;
    parentHierarchyNodeId?: number | null;
    name: string;
    reference: string | null;
    ownerEmail: string | null;
    notes: string | null;
    createdDateTime: string;
};

export type GetPartitionCallOffCodesByIdResponse = {
    callOffCodes: string[];
};

export type QueryPartitionsOptions = {
    fieldCompanyId: number;
    name?: string | undefined | null;
    reference?: string | undefined | null;
    ownerEmail?: string | undefined | null;
    pageToken: string | null;
    itemsPerPage: number | null;
    sortOrder: string;
};

export type QueryPartitionsByIdsOptions = {
    fieldCompanyId: number;
    ids: guid[];
};

export type PartitionLinkItem = {
    href: string;
    rel: string;
    method: string;
};

export type QueryPartitionsResponse = {
    totalCount: number | null;
    items: QueryPartitionsResponseItem[];
    links: PartitionLinkItem[];
};

export type QueryPartitionsResponseItem = {
    id: guid;
    parentHierarchyNodeId?: number | null;
    name: string;
    reference: string | null;
    ownerEmail: string | null;
    createdDateTime: string;
};

export type Partition = QueryPartitionsResponseItem & GetPartitionByIdResponse;

//#endregion

async function apiFetch<TResponse>(path: string, options: RequestInit, appendBaseApiUrl = true): Promise<TResponse> {
    const url = appendBaseApiUrl ? `${baseApiUrl()}${path}` : path;
    const accessToken = await acquireAccessToken("api://stock-partition-api", "access");
    const response = await fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${accessToken}` },
    });
    if (response.ok) {
        if (response.status === 204) {
            return {} as TResponse;
        }

        const result = await response.json();
        return result as TResponse;
    } else if (response.status === 400) {
        throw new BadRequestError(
            response.status,
            response.statusText,
            (await response.json()) as ValidationFailuresResponse
        );
    } else if (response.status === 403) {
        throw new ForbiddenError(response.status, await response.text());
    } else {
        throw new HttpError(response.status, await response.text());
    }
}

function toQuery(obj: any) {
    return Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .filter((k) => obj[k] !== null)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
        .join("&");
}

interface IHeaders{
    [key: string]: string;
}

export async function getPartitionById(id: guid, revalidateCache: boolean = false): Promise<GetPartitionByIdResponse> {
    const headers: IHeaders = {
        "Content-Type": "application/json"
    }

    if(revalidateCache){
        headers["Cache-Control"] = "no-cache, no-store, must-revalidate"
    }

    return await apiFetch<GetPartitionByIdResponse>(`/partitions/${id}`, {
        headers: headers,
    });
}

export async function getPartitionCallOffCodeById(id: guid): Promise<string[]> {
    return await apiFetch<string[]>(`/partitions/${id}/calloffcodes`, {
        headers: { "Content-Type": "application/json" },
    });
}

export async function queryPartitionsByUrl(url: string): Promise<QueryPartitionsResponse> {
    return await apiFetch<QueryPartitionsResponse>(
        url,
        {
            headers: { "Content-Type": "application/json" },
        },
        false
    );
}

export async function queryPartitions(options: QueryPartitionsOptions): Promise<QueryPartitionsResponse> {
    return await apiFetch<QueryPartitionsResponse>(`/partitions/search?${toQuery(options)}`, {
        headers: { "Content-Type": "application/json" },
    });
}

export async function queryPartitionsByIds(options: QueryPartitionsByIdsOptions): Promise<QueryPartitionsResponseItem[]> {
    const query = options.ids.map((id) => `${encodeURIComponent(id)}`).join(",");
    return await apiFetch<QueryPartitionsResponseItem[]>(`/partitions/filter?fieldCompanyId=${options.fieldCompanyId}&Ids=${query}`, {
        headers: { "Content-Type": "application/json" },
    });
}

export async function createPartition(request: CreatePartitionRequest): Promise<CreatePartitionResponse> {
    return await apiFetch<CreatePartitionResponse>("/partitions", {
        method: "POST",
        body: JSON.stringify(request),
        headers: { "Content-Type": "application/json" },
    });
}

export async function updatePartition(id: guid, request: UpdatePartitionRequest): Promise<void> {
    await apiFetch<void>(`/partitions/${id}`, {
        method: "PUT",
        body: JSON.stringify(request),
        headers: { "Content-Type": "application/json" },
    });
}

export async function updatePartitionCallOffCode(id: guid, request: UpdatePartitionCallOffCodeRequest): Promise<void> {
    return await apiFetch<void>(`/partitions/${id}/calloffcodes`, {
        method: "PUT",
        body: JSON.stringify(request.callOffCodes),
        headers: { "Content-Type": "application/json" },
    });
}
